import React, { useState } from 'react'
import {
  Flex,
  Heading,
  Text,
  TextField,
  Button,
  Card,
  Grid,
  Badge,
  Callout,
  Box,
  Separator,
} from '@radix-ui/themes'
import { MagnifyingGlassIcon, InfoCircledIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons'

import { ScreenWrapper } from '../../common/components/ScreenWrapper'
import { useGetApi } from '@/hooks/useGetApi'
import { GetApiVehicleData } from '@/interfaces/GetApiVehicleData'
import { GetApiAppraisalData } from '@/interfaces/GetApiAppraisalData'
import { formatWithThousandSep, formatDate } from '@/utils/utils'

export const PatenteScreen: React.FC = () => {
  const { fetchVehicleByPlate, getAppraisalByPlate, loading } = useGetApi()
  const [patente, setPatente] = useState('')
  const [vehicle, setVehicle] = useState<GetApiVehicleData | null>(null)
  const [appraisal, setAppraisal] = useState<GetApiAppraisalData | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!patente || patente.length < 4 || patente.length > 6) {
      setErrorMsg('Patente inválida. Debe tener entre 4 y 6 caracteres.')
      return
    }

    setErrorMsg(null)
    setVehicle(null)
    setAppraisal(null)

    try {
      const formattedPlate = patente.toUpperCase().replace(/[^A-Z0-9]/g, '')
      
      const [vehicleData, appraisalData] = await Promise.all([
        fetchVehicleByPlate(formattedPlate),
        getAppraisalByPlate(formattedPlate),
      ])

      if (vehicleData) {
        setVehicle(vehicleData)
        setAppraisal(appraisalData)
      } else {
        setErrorMsg('No se encontraron datos para esta patente.')
      }
    } catch (error) {
      setErrorMsg('Ocurrió un error al consultar la patente.')
    }
  }

  const formatCLP = (val?: number) => {
    if (val == null) return '—'
    return `$${formatWithThousandSep(val)}`
  }

  const Kpi = ({ label, value }: { label: string; value?: string | number | null }) => {
    if (value == null || value === '' || value === '—') return null
    return (
      <Flex direction="column" gap="1">
        <Text size="1" color="gray" weight="medium">
          {label}
        </Text>
        <Text size="2" weight="bold">
          {value}
        </Text>
      </Flex>
    )
  }

  const renderRTBadge = (code?: string) => {
    if (!code) return <Text color="gray">—</Text>
    const c = code.toUpperCase()
    if (c === 'A') return <Badge color="green">Aprobada</Badge>
    if (c === 'R') return <Badge color="red">Rechazada</Badge>
    return <Badge color="gray">{c}</Badge>
  }

  return (
    <ScreenWrapper>
      <Heading size="6" mb="4">Consulta Patente</Heading>
      
      <Card size="3" style={{ maxWidth: 600, marginBottom: '24px' }}>
        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="3">
            <Text size="2" weight="bold">Ingresa la patente del vehículo</Text>
            <Flex gap="3">
              <TextField.Root
                size="3"
                placeholder="Ej: SGXR43 o AB1234"
                value={patente}
                onChange={(e) => setPatente(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                maxLength={6}
                style={{ flex: 1 }}
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
              <Button size="3" type="submit" disabled={loading} style={{ cursor: 'pointer' }}>
                {loading ? 'Consultando...' : 'Consultar'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Card>

      {errorMsg && (
        <Callout.Root color="red" style={{ maxWidth: 600, marginBottom: '24px' }}>
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>{errorMsg}</Callout.Text>
        </Callout.Root>
      )}

      {vehicle && (
        <Grid columns={{ initial: '1', md: '2' }} gap="4">
          <Card size="3">
            <Flex direction="column" gap="4">
              <Flex justify="between" align="start">
                <Box>
                  <Text size="1" color="gray" style={{ textTransform: 'uppercase' }} weight="bold">
                    {vehicle.model?.typeVehicle?.name || '—'} · {vehicle.model?.typeVehicle?.category || 'LIVIANO'}
                  </Text>
                  <Heading size="5" mt="1">
                    {vehicle.model?.brand?.name || '—'} <Text color="gray">{vehicle.model?.name || '—'}</Text>
                  </Heading>
                  {vehicle.version && (
                    <Text size="2" color="gray" mt="1">
                      Versión: {vehicle.version}
                    </Text>
                  )}
                  <Text size="2" color="gray" mt="1">
                    {vehicle.year || '—'} · {vehicle.color || '—'} · {vehicle.transmission || '—'}
                  </Text>
                </Box>
                <Badge size="3" color="blue" variant="soft">
                  {vehicle.licensePlate}
                </Badge>
              </Flex>

              <Separator size="4" />

              <Grid columns="3" gap="3">
                <Kpi label="Motor" value={vehicle.engine ? `${vehicle.engine}L${vehicle.fuel ? ` · ${vehicle.fuel}` : ''}` : vehicle.fuel} />
                <Kpi label="Puertas" value={vehicle.doors} />
                <Kpi label="Kilometraje" value={vehicle.mileage != null ? `${formatWithThousandSep(vehicle.mileage)} km` : null} />
                <Kpi label="Potencia" value={vehicle.horsePower ? `${vehicle.horsePower} HP` : null} />
                <Kpi label="Torque" value={vehicle.torque ? `${vehicle.torque} Nm` : null} />
                <Kpi label="Tracción" value={vehicle.traction} />
              </Grid>

              <Separator size="4" />

              <Box>
                <Flex justify="between" align="center" mb="2">
                  <Text size="2" weight="bold">Revisión técnica</Text>
                  <Flex gap="2" align="center">
                    <Text size="1" color="gray">Mecánica:</Text>
                    {renderRTBadge(vehicle.rtResult)}
                    <Text size="1" color="gray" ml="2">Gases:</Text>
                    {renderRTBadge(vehicle.rtResultGas)}
                  </Flex>
                </Flex>
                
                {(vehicle.rtDate || vehicle.monthRT || vehicle.plantaRevisora) ? (
                  <Grid columns="3" gap="3">
                    <Kpi label="Último control" value={vehicle.rtDate ? formatDate(vehicle.rtDate) : '—'} />
                    <Kpi label="Mes vencimiento" value={vehicle.monthRT} />
                    <Kpi label="Planta" value={vehicle.plantaRevisora ? `${vehicle.plantaRevisora.codPrt || ''} ${vehicle.plantaRevisora.comuna ? `· ${vehicle.plantaRevisora.comuna}` : ''}` : null} />
                  </Grid>
                ) : (
                  <Text size="2" color="gray">Sin información de revisión técnica en la base nacional para esta patente.</Text>
                )}
              </Box>

              <Separator size="4" />

              <Grid columns="2" gap="3">
                <Box>
                  <Text size="1" color="gray" weight="bold">VIN</Text>
                  <Text size="2" as="div">{vehicle.vinNumber || '—'}</Text>
                </Box>
                <Box>
                  <Text size="1" color="gray" weight="bold">Motor N°</Text>
                  <Text size="2" as="div">{vehicle.engineNumber || '—'}</Text>
                </Box>
              </Grid>
            </Flex>
          </Card>

          <Card size="3">
            <Flex direction="column" gap="4">
              <Heading size="4">Tasación</Heading>
              
              {appraisal ? (
                <>
                  <Box>
                    <Text size="1" color="gray" weight="bold" style={{ textTransform: 'uppercase' }}>Precio usado de mercado</Text>
                    <Heading size="6" mt="1" color="green">
                      {formatCLP(appraisal.precioUsado?.precio)}
                    </Heading>
                    <Text size="2" color="gray">
                      Banda: {formatCLP(appraisal.precioUsado?.banda_min)} — {formatCLP(appraisal.precioUsado?.banda_max)}
                    </Text>
                  </Box>

                  <Separator size="4" />

                  <Flex direction="column" gap="2">
                    <Flex justify="between">
                      <Text size="2" color="gray">Precio retoma</Text>
                      <Text size="2" weight="bold">{formatCLP(appraisal.precioRetoma)}</Text>
                    </Flex>
                    <Flex justify="between">
                      <Text size="2" color="gray">Tasación fiscal</Text>
                      <Text size="2" weight="bold">{formatCLP(appraisal.informacionFiscal?.tasacion)}</Text>
                    </Flex>
                    <Flex justify="between">
                      <Text size="2" color="gray">Año fiscal</Text>
                      <Text size="2" weight="bold">{appraisal.informacionFiscal?.ano_info_fiscal || '—'}</Text>
                    </Flex>
                    {appraisal.informacionFiscal?.permiso && (
                      <Flex justify="between">
                        <Text size="2" color="gray">Permiso circulación</Text>
                        <Text size="2" weight="bold">{formatCLP(Number(appraisal.informacionFiscal.permiso))}</Text>
                      </Flex>
                    )}
                  </Flex>
                </>
              ) : (
                <Text size="2" color="gray">Sin datos de tasación disponibles para esta patente.</Text>
              )}

              <Separator size="4" />

              <Callout.Root size="1" color="gray">
                <Callout.Icon>
                  <InfoCircledIcon />
                </Callout.Icon>
                <Callout.Text>
                  Datos reales vía base nacional de vehículos. Verifica siempre en fuentes oficiales.
                </Callout.Text>
              </Callout.Root>
            </Flex>
          </Card>
        </Grid>
      )}
    </ScreenWrapper>
  )
}

export default PatenteScreen
